import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Stack } from '@mui/material';
import { getSystemDashboardSummary } from '../api/authApi';
import { colorTokens } from '../app/designTokens';
import { AttentionList } from '../components/dashboard/AttentionList';
import {
  DashboardEmptyState,
  MiniSegmentedBar,
  MiniStatusDonut,
  MiniTrendPlaceholder,
} from '../components/dashboard/DashboardVisuals';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { MetricCard } from '../components/MetricCard';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { Button } from '../components/ui/Button';
import {
  buildSystemAttentionItems,
  mapSystemDashboardSummary,
} from '../utils/dashboardSummaryMappers';

const { semantic } = colorTokens;

const metricGridSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(3, minmax(0, 1fr))',
    lg: 'repeat(4, minmax(0, 1fr))',
  },
  gap: 2,
  width: '100%',
  alignItems: 'stretch',
};

const twoColSx = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: '1fr 1fr',
  },
  gap: 2.5,
  width: '100%',
};

const EMPTY_STATS = {
  active: null,
  trial: null,
  pastDue: null,
  blocked: null,
  pendingPayments: null,
  notReadyTenants: null,
  readyTenants: null,
  warningTenants: null,
  provisioningFailed: null,
  schemaFailed: null,
  openTickets: null,
  attention: [],
};

export function DashboardPage() {
  const { t } = useTranslation(['dashboard', 'navigation', 'common']);
  const emptyValue = t('common:empty_value');
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);

  const navCards = useMemo(() => [
    { title: t('navigation:customers'), description: t('dashboard:customers_description'), to: '/customers' },
    { title: t('navigation:tenants'), description: t('dashboard:tenants_description'), to: '/tenants' },
    { title: t('navigation:support'), description: t('dashboard:support_description'), to: '/support' },
    { title: t('navigation:modulePricing'), description: t('dashboard:plans_description'), to: '/billing/module-prices' },
  ], [t]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getSystemDashboardSummary()
      .then((summary) => {
        if (!cancelled) {
          setStats(mapSystemDashboardSummary(summary));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStats(EMPTY_STATS);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const kpiCards = useMemo(() => [
    {
      title: t('dashboard:active_subscriptions'),
      value: stats.active ?? emptyValue,
      to: '/subscriptions',
      helper: stats.active == null ? t('dashboard:metric_unavailable') : undefined,
    },
    {
      title: t('dashboard:trial_tenants'),
      value: stats.trial ?? emptyValue,
      to: '/subscriptions',
      helper: stats.trial == null ? t('dashboard:metric_unavailable') : undefined,
    },
    {
      title: t('dashboard:past_due'),
      value: stats.pastDue ?? emptyValue,
      to: '/subscriptions',
      helper: stats.pastDue == null ? t('dashboard:metric_unavailable') : undefined,
    },
    {
      title: t('dashboard:payment_blocked'),
      value: stats.blocked ?? emptyValue,
      to: '/subscriptions',
      helper: stats.blocked == null ? t('dashboard:metric_unavailable') : undefined,
    },
    {
      title: t('dashboard:pending_payments'),
      value: stats.pendingPayments ?? emptyValue,
      to: '/payments',
      helper: stats.pendingPayments == null ? t('dashboard:metric_unavailable') : undefined,
    },
    {
      title: t('dashboard:tenants_not_ready'),
      value: stats.notReadyTenants ?? emptyValue,
      to: '/provisioning/readiness?overall_status=not_ready',
      helper: stats.notReadyTenants == null ? t('dashboard:metric_unavailable') : undefined,
    },
    {
      title: t('dashboard:open_support_tickets'),
      value: stats.openTickets ?? emptyValue,
      to: '/support',
      helper: stats.openTickets == null ? t('dashboard:metric_unavailable') : undefined,
    },
  ], [emptyValue, stats, t]);

  const billingAvailable = [
    stats.active,
    stats.trial,
    stats.pastDue,
    stats.blocked,
    stats.pendingPayments,
  ].some((value) => typeof value === 'number');

  const billingSegments = useMemo(() => [
    {
      key: 'active',
      label: t('dashboard:segment_active'),
      value: stats.active || 0,
      color: semantic.success,
    },
    {
      key: 'trial',
      label: t('dashboard:segment_trial'),
      value: stats.trial || 0,
      color: semantic.info,
    },
    {
      key: 'past_due',
      label: t('dashboard:segment_past_due'),
      value: stats.pastDue || 0,
      color: semantic.warning,
    },
    {
      key: 'blocked',
      label: t('dashboard:segment_blocked'),
      value: stats.blocked || 0,
      color: semantic.error,
    },
    {
      key: 'pending',
      label: t('dashboard:segment_pending'),
      value: stats.pendingPayments || 0,
      color: colorTokens.brand.primary,
    },
  ], [stats, t]);

  const readinessAvailable = [
    stats.readyTenants,
    stats.warningTenants,
    stats.notReadyTenants,
  ].some((value) => typeof value === 'number');

  const readinessSegments = useMemo(() => [
    {
      key: 'ready',
      label: t('dashboard:readiness_ready'),
      value: stats.readyTenants || 0,
      color: semantic.success,
    },
    {
      key: 'warning',
      label: t('dashboard:readiness_warning'),
      value: stats.warningTenants || 0,
      color: semantic.warning,
    },
    {
      key: 'not_ready',
      label: t('dashboard:readiness_not_ready'),
      value: stats.notReadyTenants || 0,
      color: semantic.error,
    },
  ], [stats, t]);

  const attentionItems = useMemo(
    () => buildSystemAttentionItems(stats.attention, t),
    [stats.attention, t],
  );

  const billingTotal = billingSegments.reduce((sum, item) => sum + item.value, 0);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('dashboard:title')}
        subtitle={t('dashboard:subtitle')}
        actions={(
          <Button
            variant="Primary"
            component={RouterLink}
            to="/onboarding/trial"
          >
            {t('dashboard:new_trial_tenant')}
          </Button>
        )}
      />

      <Stack spacing={2.5}>
        <SectionCard
          variant="Default"
          title={t('dashboard:kpi_overview')}
          subtitle={t('dashboard:kpi_overview_subtitle')}
          sx={{ mb: 0 }}
        >
          {loading ? (
            <LoadingSkeleton type="CardGrid" rows={4} />
          ) : (
            <Box sx={metricGridSx}>
              {kpiCards.map((card) => (
                <MetricCard
                  key={card.title}
                  value={card.value}
                  label={card.title}
                  helper={card.helper || ''}
                  to={card.to}
                />
              ))}
            </Box>
          )}
        </SectionCard>

        <Box sx={twoColSx}>
          <SectionCard
            variant="Default"
            title={t('dashboard:billing_health')}
            subtitle={t('dashboard:billing_health_subtitle')}
            actions={(
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="Ghost" component={RouterLink} to="/subscriptions" sx={{ minHeight: 32 }}>
                  {t('navigation:subscriptions')}
                </Button>
                <Button variant="Ghost" component={RouterLink} to="/payments" sx={{ minHeight: 32 }}>
                  {t('navigation:payments')}
                </Button>
                <Button variant="Ghost" component={RouterLink} to="/billing/module-prices" sx={{ minHeight: 32 }}>
                  {t('navigation:modulePricing')}
                </Button>
              </Stack>
            )}
            sx={{ mb: 0 }}
          >
            {loading ? (
              <LoadingSkeleton type="Detail" />
            ) : !billingAvailable ? (
              <DashboardEmptyState message={t('dashboard:metric_unavailable')} />
            ) : billingTotal > 0 ? (
              <MiniStatusDonut
                segments={billingSegments}
                centerValue={billingTotal}
                centerLabel={t('dashboard:billing_total_label')}
              />
            ) : (
              <DashboardEmptyState message={t('dashboard:billing_health_empty')} />
            )}
          </SectionCard>

          <SectionCard
            variant="Default"
            title={t('dashboard:tenant_readiness')}
            subtitle={t('dashboard:tenant_readiness_subtitle')}
            actions={(
              <Button
                variant="Ghost"
                component={RouterLink}
                to="/provisioning/readiness"
                sx={{ minHeight: 32 }}
              >
                {t('dashboard:open_readiness')}
              </Button>
            )}
            sx={{ mb: 0 }}
          >
            {loading ? (
              <LoadingSkeleton type="Detail" />
            ) : readinessAvailable ? (
              <MiniSegmentedBar segments={readinessSegments} />
            ) : (
              <DashboardEmptyState message={t('dashboard:readiness_unavailable')} />
            )}
          </SectionCard>
        </Box>

        <Box sx={twoColSx}>
          <SectionCard
            variant="Default"
            title={t('dashboard:attention_title')}
            subtitle={t('dashboard:attention_subtitle')}
            sx={{ mb: 0 }}
          >
            {loading ? (
              <LoadingSkeleton type="Detail" />
            ) : (
              <AttentionList
                items={attentionItems}
                emptyMessage={t('dashboard:attention_empty')}
              />
            )}
          </SectionCard>

          <SectionCard
            variant="Default"
            title={t('dashboard:trends_title')}
            subtitle={t('dashboard:trends_subtitle')}
            sx={{ mb: 0 }}
          >
            <MiniTrendPlaceholder message={t('dashboard:trend_placeholder')} />
          </SectionCard>
        </Box>

        <SectionCard
          variant="Default"
          title={t('dashboard:quick_navigation')}
          subtitle={t('dashboard:quick_navigation_subtitle')}
          sx={{ mb: 0 }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 2,
              width: '100%',
            }}
          >
            {navCards.map((card) => (
              <MetricCard
                key={card.title}
                value={card.title}
                label={card.description}
                to={card.to}
              />
            ))}
          </Box>
        </SectionCard>
      </Stack>
    </Box>
  );
}
